/**
 *
 * IBM Confidential
 *
 * (C) Copyright IBM Corp. 2019, 2023
 *
 * The source code for this program is not published or otherwise
 * divested of its trade secrets, irrespective of what has been
 * deposited with the U. S. Copyright Office
 *
 * US Government Users Restricted Rights - Use, duplication or
 * disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 *
 **/

import axios from 'axios';
import urlJoin from 'proper-url-join';

import { ReactivateSession } from '../../../../ts/session';
import { getAccessPrefixedUrl } from '../../../ia/util/access-control';
import { endpoints } from '../../../ia/util/config';
import {
  transformParameters,
  serializeParameters as paramsSerializer
} from '../../../ia/util/rest';
import {
  createdObjectEvents,
  pageEvents
} from '../../../ia/util/trackers/segment';
import { validation } from '../../../ia/util/validation';
import { nodeFactory } from './factories';

const nodeEndpoint = (
  skillId: string,
  useNewIAUpdatedAccess: boolean,
  assistantId: string,
  nodeId: string
): string =>
  nodesEndpoint(skillId, useNewIAUpdatedAccess, assistantId) + '/' + nodeId;

const nodesEndpoint = (
  skillId: string,
  useNewIAUpdatedAccess: boolean,
  assistantId: string
): string =>
  urlJoin(
    endpoints.store,
    getAccessPrefixedUrl(
      useNewIAUpdatedAccess,
      assistantId,
      `skills/${skillId}/workspace/dialog_nodes`
    )
  );

export function listNodes(
  skillId: string,
  useNewIAUpdatedAccess: boolean,
  assistantId: string,
  options: any,
  reactivateSession: ReactivateSession = ReactivateSession.NEVER
) {
  const validationErrors = validation.validateGuid(skillId);
  if (validationErrors) {
    return Promise.reject({ data: { error: validationErrors } });
  }
  const config = {
    params: transformParameters(options),
    paramsSerializer
  };
  return axios
    .get(
      urlJoin(nodesEndpoint(skillId, useNewIAUpdatedAccess, assistantId), {
        query: {
          sort: 'depth_first',
          reactivateSession
        }
      }),
      config
    )
    .then(response => response.data.dialog_nodes.map(node => nodeFactory(node)))
    .catch(error => Promise.reject(error));
}

export function createNode(
  skillId: string,
  useNewIAUpdatedAccess: boolean,
  assistantId: string,
  node: any,
  options: any
) {
  const validationErrors = validation.validateGuid(skillId);
  if (validationErrors) {
    return Promise.reject({ data: { error: validationErrors } });
  }

  const config = {
    params: transformParameters(options),
    paramsSerializer
  };
  return axios
    .post(
      nodesEndpoint(skillId, useNewIAUpdatedAccess, assistantId),
      node,
      config
    )
    .then(response => {
      createdObjectEvents.dialogNode(
        {
          object: node.dialog_node,
          resultValue: 'success',
          successFlag: true
        },
        {
          waNodeGeneration: node && !node.parent ? 'parent' : 'child'
        }
      );
      pageEvents.dialogNodeOpened();
      return nodeFactory(response.data);
    })
    .catch(error => {
      createdObjectEvents.dialogNode(
        {
          object: node.dialog_node,
          resultValue: error,
          successFlag: false
        },
        {
          waNodeGeneration: node && !node.parent ? 'parent' : 'child'
        }
      );
      return Promise.reject(error);
    });
}

export function deleteNode(
  skillId: string,
  useNewIAUpdatedAccess: boolean,
  assistantId: string,
  nodeId: string
) {
  const validationErrors = validation.validateGuid(skillId);
  if (validationErrors) {
    return Promise.reject({ data: { error: validationErrors } });
  }
  return axios
    .delete(nodeEndpoint(skillId, useNewIAUpdatedAccess, assistantId, nodeId))
    .then(() => Promise.resolve())
    .catch(error => Promise.reject(error));
}

export function updateNode(
  skillId: string,
  useNewIAUpdatedAccess: boolean,
  assistantId: string,
  nodeId: string,
  node: any
) {
  const validationErrors = validation.validateGuid(skillId);
  if (validationErrors) {
    return Promise.reject({ data: { error: validationErrors } });
  }
  const params = {
    include_audit: true
  };
  const config = { params };

  return axios
    .post(
      nodeEndpoint(skillId, useNewIAUpdatedAccess, assistantId, nodeId),
      node,
      config
    )
    .then(response => nodeFactory(response.data))
    .catch(error => Promise.reject(error));
}
